import { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';

const VARIANTS = {
  primary:
    'bg-brand-950 text-white hover:bg-brand-900 active:bg-brand-950 shadow-xs disabled:bg-ink-300 dark:disabled:bg-ink-800',
  secondary:
    'bg-[var(--surface-raised)] text-[var(--text-primary)] border border-[var(--border-default)] hover:bg-[var(--surface-sunken)] shadow-xs',
  accent: 'bg-accent-600 text-white hover:bg-accent-700 active:bg-accent-800 shadow-xs',
  ghost: 'text-[var(--text-secondary)] hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]',
  danger: 'bg-danger-600 text-white hover:bg-danger-700 active:bg-danger-700 shadow-xs',
  dangerOutline:
    'border border-danger-200 text-danger-700 hover:bg-danger-50 dark:border-danger-700/40 dark:text-danger-500 dark:hover:bg-danger-500/10',
  link: 'text-accent-600 hover:text-accent-700 hover:underline underline-offset-4 dark:text-accent-400',
};

const SIZES = {
  xs: 'h-7 px-2.5 text-xs gap-1.5 rounded-md',
  sm: 'h-9 px-3.5 text-sm gap-1.5 rounded-lg',
  md: 'h-10 px-4 text-sm gap-2 rounded-lg',
  lg: 'h-11 px-5 text-[15px] gap-2 rounded-lg',
};

const ICON_SIZES = { xs: 'size-7', sm: 'size-9', md: 'size-10', lg: 'size-11' };

/**
 * Tombol dasar aplikasi.
 *
 * Saat `loading`, tombol dinonaktifkan sekaligus diberi `aria-busy` - pembaca
 * layar mengumumkan status sibuk, dan klik ganda tidak mungkin terjadi. Lebar
 * tombol tidak berubah karena label tetap ada di tempatnya, hanya tersembunyi
 * secara visual di balik indikator.
 */
export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    icon: Icon,
    iconRight: IconRight,
    loading = false,
    iconOnly = false,
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex items-center justify-center font-semibold whitespace-nowrap',
        'transition-[background-color,border-color,color,box-shadow,transform] duration-150',
        'active:scale-[0.985]',
        'disabled:pointer-events-none disabled:opacity-55',
        iconOnly ? cn(ICON_SIZES[size], 'rounded-lg p-0') : SIZES[size],
        VARIANTS[variant],
        className,
      )}
      {...props}
    >
      {loading && (
        <Loader2
          className="absolute size-4 animate-spin"
          aria-hidden="true"
        />
      )}
      <span
        className={cn(
          'inline-flex items-center gap-[inherit]',
          loading && 'invisible',
        )}
      >
        {Icon && <Icon className={size === 'xs' ? 'size-3.5' : 'size-4'} aria-hidden="true" />}
        {children}
        {IconRight && (
          <IconRight className={size === 'xs' ? 'size-3.5' : 'size-4'} aria-hidden="true" />
        )}
      </span>
    </button>
  );
});
